import { collectWebAttributionContext } from "@punchup/analytics/attribution-context";
import { getApiClient } from "@punchup/utils/apiRegistry";

interface FollowComedianResponse {
  success: boolean;
  followLocation?: string;
}

interface UnfollowComedianResponse {
  success: boolean;
  unfollowLocation?: string;
}

/**
 * Follow a comedian via the v2 API
 *
 * @param comedianId - The comedian's ID to follow
 * @param followLocation - Optional analytics tracking location
 * @param ctaSource - Optional analytics CTA source identifier
 * @param pageSection - Optional analytics page section identifier
 * @returns Promise resolving to success status and the followLocation echoed back
 *
 * The browser attribution context (referrer, UTMs, short-link) rides along in
 * the body so the server-tracked "Followed Comedian" event keeps the referrer
 * props mixpanel-browser used to attach when this event was client-tracked.
 */
export async function followComedianById(
  comedianId: string,
  followLocation?: string,
  ctaSource?: string,
  pageSection?: string
): Promise<FollowComedianResponse> {
  const result = await getApiClient().put<{ data: FollowComedianResponse }>(
    `/api/v2/user/comedians/${comedianId}/subscribe`,
    {
      followLocation,
      ctaSource,
      pageSection,
      attributionContext: collectWebAttributionContext(),
    }
  );

  return result.data;
}

/**
 * Unfollow a comedian via the v2 API
 *
 * @param comedianId - The comedian's ID to unfollow
 * @param unfollowLocation - Optional analytics tracking location
 * @returns Promise resolving to success status and the unfollowLocation echoed back
 */
export async function unfollowComedianById(
  comedianId: string,
  unfollowLocation?: string
): Promise<UnfollowComedianResponse> {
  const result = await getApiClient().del<{
    data: UnfollowComedianResponse;
  }>(`/api/v2/user/comedians/${comedianId}/subscribe`, {
    unfollowLocation,
    attributionContext: collectWebAttributionContext(),
  });

  return result.data;
}

/**
 * Follow multiple comedians via the v2 API
 *
 * Calls the single follow API for each comedian in parallel.
 *
 * @param comedianIds - Array of comedian IDs to follow
 * @param followLocation - Optional analytics tracking location
 * @returns Promise resolving to array of results
 */
export async function followComediansByIds(
  comedianIds: string[],
  followLocation?: string
): Promise<FollowComedianResponse[]> {
  const results = await Promise.all(
    comedianIds.map((id) => followComedianById(id, followLocation))
  );

  return results;
}
